import {
  Component,
  HostListener,
  OnInit,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  UrlTree,
} from '@angular/router';
import { User } from '@angular/fire/auth';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  filter,
  from,
  map,
  Observable,
  of,
  skip,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { RouterModule } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { AuthService } from '@app/core/services/auth.service';
import { CollaboratorService } from '@app/core/services/collaborator.service';
import { isAdminUser } from '@app/core/admin/admin.config';
import { environment } from '../../../environment';
import { UserMenuComponent } from '@app/shared/user-menu/user-menu.component';
import { SettingsDialogComponent } from '@app/shared/settings-dialog/settings-dialog.component';
import { WaitlistDialogComponent } from '@app/shared/waitlist-dialog/waitlist-dialog.component';


@Component({
    selector: 'app-header',
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatMenuModule,
        MatDividerModule,
        MatProgressBarModule,
        MatTooltipModule,
        UserMenuComponent,
        RouterModule,
    ],
    templateUrl: './header.component.html',
    styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit {
  isScrolled = false;
  menuOpen = false;
  user!: User | null;
  user$!: Observable<any>;
  statusMessage!: string;
  isLoading: boolean = false;
  isBeta: boolean = false;

  activeFragment: string | null = null;

  /**
   * Whether the CURRENT signed-in user is an active Execution Partner —
   * drives the "Partner Dashboard" vs. "Become a Partner" nav link (both
   * desktop and mobile menu). Resolved via the same
   * CollaboratorService.resolvePartnerAccess() used everywhere else a
   * partner is identified (project-new.component.ts, home-new.component.ts,
   * footer.component.ts), so "partner" means the same thing site-wide.
   * Only an 'active' collaborator record counts — pending/paused/
   * offboarded all resolve to false, same as the workspace header.
   *
   * ASSUMPTION: uses `this.auth.user$` (the raw Firebase Auth User
   * observable) — matches the pattern already used elsewhere in this
   * codebase. If AuthService exposes this under a different name, this
   * is a one-line fix.
   */
  isPartnerViewer = false;

  /**
   * Whether the current signed-in user is an admin — drives a distinct
   * "Admin" chip alongside (never instead of) the partner chip, since
   * the two statuses are independent (an admin isn't necessarily also a
   * partner, and vice versa).
   *
   * ASSUMPTION: imports isAdminUser from '@app/core/admin/admin.config' —
   * inferred from project-member-guard.ts's relative import
   * ('../admin/admin.config'), not independently confirmed. If your
   * actual path differs, this is a one-line fix.
   */
  isAdmin = false;

  constructor(
    private dialog: MatDialog,
    private snack: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private collaboratorsSvc: CollaboratorService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      if (!environment.production) this.isBeta = true;
      console.log(this.isBeta);
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        // Get the fragment from the URL (e.g., 'community')
        const tree: UrlTree = this.router.parseUrl(this.router.url);
        this.activeFragment = tree.fragment;
      });
  }

  isCommunityActive(): boolean {
    return (
      this.router.url.split('#')[0] === '/' &&
      this.activeFragment === 'community'
    );
  }

  ngOnInit(): void {
    // this.user$ = this.auth.user$;
    this.user$ = this.auth.userDoc$();

    // Resolves BOTH admin and partner status off the same auth-user
    // stream, in one subscription — admin is a synchronous check
    // (isAdminUser), partner status needs a Firestore round trip
    // (resolvePartnerAccess), so they're combined via switchMap rather
    // than two independent subscriptions racing each other.
    this.auth.user$
      .pipe(
        switchMap((authUser) => {
          if (!authUser) {
            return of({ isAdmin: false, collaborator: null as any });
          }
          const isAdmin = isAdminUser(authUser.email, authUser.uid);
          return from(
            this.collaboratorsSvc.resolvePartnerAccess(authUser.uid, authUser.email),
          ).pipe(map((collaborator) => ({ isAdmin, collaborator })));
        }),
      )
      .subscribe(({ isAdmin, collaborator }) => {
        this.isAdmin = isAdmin;
        this.isPartnerViewer = !!collaborator && collaborator.status === 'active';
      });

    // Optional: handle query params
    // this.route.queryParams.subscribe((params) => {
    //   const settings = params['settings'];
    //   if (settings) {
    //     this.openSettingsDialog(settings);
    //   }
    // });
  }

  openSettingsDialog(item: string) {
    const dialogRef = this.dialog.open(SettingsDialogComponent, {
      panelClass: 'settings-dialog',
      maxWidth: '100vw',
      data: {
        selectedItem: item,
      },
    });

    // dialogRef.afterClosed().subscribe(() => {
    //   this.router.navigate(['/'], {
    //     queryParams: { settings: null },
    //   });
    // });
  }

  goToSettings(tab: string) {
    this.router.navigate([], {
      queryParams: { settings: tab },
      queryParamsHandling: 'merge',
    });
  }

  /** Routes to the Execution Partner dashboard — only ever shown/enabled
   *  for a resolved active partner (see isPartnerViewer). */
  goToPartnerDashboard(): void {
    this.router.navigateByUrl('/execution-partners/dashboard');
  }

  /** Routes to the Execution Partner landing/apply page — shown to
   *  everyone who ISN'T already a resolved active partner. */
  goToBecomePartner(): void {
    this.router.navigateByUrl('/execution-partners');
  }

  /** Routes to the admin hub — only ever shown/enabled for a resolved
   *  admin (see isAdmin).
   *  ASSUMPTION: '/admin' — not independently confirmed, adjust if your
   *  actual admin route differs. */
  goToAdminHub(): void {
    this.router.navigateByUrl('/admin');
  }

  get firstWord(): string {
    return this.user?.displayName?.split(' ')[0] || '';
  }

  // Inside HeaderComponent
  login() {
    // No more popups here, just send them to the login page
    this.router.navigate(['/login']);
  }

  login2() {
    this.isLoading = true;
    this.statusMessage = 'Please complete the action in the popup window...';

    const waitingTimeout = setTimeout(() => {
      this.statusMessage =
        'Hang tight… hope you didn’t close the sign-in window !';
    }, 5000);

    this.auth.signInWithGoogle().subscribe({
      next: (user) => {
        clearTimeout(waitingTimeout);

        if (user) {
          this.user = user;
          console.log('firebase sends this after we authenticate:', user);

          this.statusMessage = 'Signed in successfully!';
        } else {
          this.statusMessage =
            'Ohh, you actually cancelled the sign-in! No worries 😎 Hope to see you again soon!';
        }

        this.isLoading = false;
      },
      error: () => {
        clearTimeout(waitingTimeout);
        this.statusMessage = 'Something went wrong. Please try again.';
        this.isLoading = false;
      },
    });
  }

  logout() {
    this.auth.signOut().subscribe((res) => {
      console.log('user logged out.');
    });
  }

  snackMsg() {
    this.snack.showInfo('Dark theme comig soon..', 2000);
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    this.isScrolled = scrollY > 50;
  }

  navHome() {
    this.router.navigateByUrl('');
  }

  scrollToSection(id: string) {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  openDialog(enter: string, exit: string) {
    this.dialog.open(WaitlistDialogComponent, {
      enterAnimationDuration: enter,
      exitAnimationDuration: exit,
      width: '700px',
    });
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;

    if (this.menuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }

  showMessage(msg: string) {
    // Logic to close menu even when just showing a message
    this.menuOpen = false;
    document.body.classList.remove('no-scroll');

    this.snack.showSuccess(msg);
  }

  navigate(section: string) {
    console.log('User clicked on:', section);

    // Close menu
    this.menuOpen = false;
    document.body.classList.remove('no-scroll');

    // Delay scroll slightly to allow re-rendering / animation
    setTimeout(() => {
      this.scrollToSection(section.toLowerCase());
    }, 300); // Adjust delay as needed (300ms is often sufficient)
  }
}